import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { CapacitorCalendar } from '@ebarooni/capacitor-calendar';
import { Casa, Tarea } from '../types';
import { claveDia, esMia, yoEn } from './tareas';

/**
 * Las tareas que te tocan, en la app Recordatorios de iOS (EventKit, con
 * @ebarooni/capacitor-calendar). Va en una lista propia, «myarinn · Tareas»,
 * con un recordatorio por tarea: la próxima vez que toca. Al tacharlo en
 * Recordatorios, la próxima vez que abres myarinn se marca hecha para toda la
 * casa y se pone el siguiente.
 *
 * iOS no deja a la app enterarse en segundo plano: lo tachado llega al abrirla.
 * Qué recordatorio es de qué tarea se guarda en el dispositivo (Preferences).
 */

export const LISTA = 'myarinn · Tareas';
const CLAVE = 'recordatoriosTareas';
const COLOR = '#C2410C';

/** Recordatorio puesto para una tarea. id null: lo borraste en Recordatorios y no se repone hasta la vez siguiente. */
export interface Puesto { id: string | null; fecha: string; titulo: string; hora?: string }
export type Mapa = Record<string, Puesto>;

interface Ajustes { activo: boolean; listId?: string; mapa: Mapa }

export const esIOS = Capacitor.getPlatform() === 'ios';

const leer = async (): Promise<Ajustes> => {
  const { value } = await Preferences.get({ key: CLAVE });
  try { return value ? { activo: false, mapa: {}, ...JSON.parse(value) } : { activo: false, mapa: {} }; }
  catch { return { activo: false, mapa: {} }; }
};
const guardar = (a: Ajustes) => Preferences.set({ key: CLAVE, value: JSON.stringify(a) });

export const recordatoriosActivos = async () => esIOS && (await leer()).activo;

export const tituloDe = (t: Tarea) => `${t.icono} ${t.nombre}`;

export interface Plan {
  hechas: Tarea[];
  crear: Tarea[];
  cambiar: { tarea: Tarea; id: string }[];
  borrar: string[];
  mapa: Mapa;
}

/**
 * Qué hay que hacer para que Recordatorios cuadre con las tareas. Pura, para
 * probarla (recordatoriosTareas.test.ts).
 * - `mias`: las tareas activas que te tocan.
 * - `existentes`: los recordatorios de la lista, por id.
 */
export function planRecordatorios(
  mias: Tarea[], mapaViejo: Mapa, existentes: Map<string, { isCompleted: boolean }>,
): Plan {
  const plan: Plan = { hechas: [], crear: [], cambiar: [], borrar: [], mapa: {} };
  const porId = new Map(mias.map(t => [t.id, t]));
  const recienHechas = new Set<string>();
  const usados = new Set<string>();

  for (const [tareaId, p] of Object.entries(mapaViejo)) {
    const t = porId.get(tareaId);
    const r = p.id ? existentes.get(p.id) : undefined;
    if (p.id && r?.isCompleted) {
      // Tachado en Recordatorios: se marca si sigue siendo la misma vez
      if (t && claveDia(t.fecha) === p.fecha) { plan.hechas.push(t); recienHechas.add(t.id); }
      plan.borrar.push(p.id);
      continue;
    }
    if (!t) { if (p.id && r) plan.borrar.push(p.id); continue; }
    if (p.id && !r) { plan.mapa[tareaId] = { ...p, id: null }; continue; }
    plan.mapa[tareaId] = p;
    if (p.id) usados.add(p.id);
  }

  for (const t of mias) {
    // La recién hecha cambiará de fecha al marcarla: su siguiente va en la próxima vuelta
    if (recienHechas.has(t.id)) continue;
    const p = plan.mapa[t.id];
    const fecha = claveDia(t.fecha);
    const titulo = tituloDe(t);
    if (!p || (p.id === null && p.fecha !== fecha)) { plan.crear.push(t); delete plan.mapa[t.id]; continue; }
    if (p.id && (p.fecha !== fecha || p.titulo !== titulo || (p.hora ?? '') !== (t.hora ?? ''))) {
      plan.cambiar.push({ tarea: t, id: p.id });
      plan.mapa[t.id] = { id: p.id, fecha, titulo, hora: t.hora };
    }
  }

  // Lo que hay en la lista sin ser de ninguna tarea (de otra instalación): fuera
  existentes.forEach((r, id) => {
    if (!usados.has(id) && !plan.borrar.includes(id) && !plan.cambiar.some(c => c.id === id)) plan.borrar.push(id);
  });
  return plan;
}

/** Cuándo vence: el día de la tarea, a su hora o a las 9:00. */
const vence = (t: Tarea) => {
  const [h, m] = (t.hora && /^\d{2}:\d{2}$/.test(t.hora) ? t.hora : '09:00').split(':').map(Number);
  const f = t.fecha;
  return new Date(f.getFullYear(), f.getMonth(), f.getDate(), h, m).getTime();
};

const datos = (t: Tarea, casa: Casa | undefined) => ({
  title: tituloDe(t),
  dueDate: vence(t),
  // Con hora, avisa a esa hora; sin hora ya avisa myarinn por la mañana
  alerts: t.hora ? [0] : null,
  notes: casa && !casa.personal ? `${casa.icono} ${casa.nombre} · myarinn` : 'myarinn',
});

/** Pide permiso y prepara la lista. 'denegado' si iOS no lo da. */
export async function activarRecordatorios(): Promise<'ok' | 'denegado'> {
  const { result } = await CapacitorCalendar.requestFullRemindersAccess();
  if (result !== 'granted') return 'denegado';
  // La lista sobrevive a reinstalar la app, pero no qué recordatorio era de qué
  // tarea: se empieza de cero para no duplicarlos.
  const { result: listas } = await CapacitorCalendar.getRemindersLists();
  const vieja = listas.find(l => l.title === LISTA);
  if (vieja) await CapacitorCalendar.deleteRemindersList({ id: vieja.id }).catch(() => {});
  const lista = await CapacitorCalendar.createRemindersList({ title: LISTA, color: COLOR });
  await guardar({ activo: true, listId: lista.id, mapa: {} });
  return 'ok';
}

/** Apaga y borra la lista con sus recordatorios. */
export async function desactivarRecordatorios(): Promise<void> {
  const a = await leer();
  if (a.listId) await CapacitorCalendar.deleteRemindersList({ id: a.listId }).catch(() => {});
  await guardar({ activo: false, mapa: {} });
}

let enCurso: Promise<void> | null = null;

/**
 * Pone Recordatorios al día y marca hechas las que tachaste allí. Solo hace
 * algo en iOS, con la opción encendida y con Plus. Nunca dos a la vez.
 */
export function sincronizarRecordatorios(opciones: {
  tareas: Tarea[]; casas: Casa[]; uid: string | undefined; conPlus: boolean;
  marcarHecha: (t: Tarea, participanteId: string | undefined) => Promise<void>;
}): Promise<void> {
  if (!esIOS || !opciones.uid || !opciones.conPlus) return Promise.resolve();
  if (enCurso) return enCurso;
  enCurso = sincronizar(opciones).finally(() => { enCurso = null; });
  return enCurso;
}

async function sincronizar({ tareas, casas, uid, marcarHecha }: Parameters<typeof sincronizarRecordatorios>[0]) {
  const a = await leer();
  if (!a.activo || !a.listId) return;
  let listId = a.listId;
  let mapa = a.mapa;

  let existentes = new Map<string, { isCompleted: boolean }>();
  try {
    const { result } = await CapacitorCalendar.getRemindersFromLists({ listIds: [listId] });
    existentes = new Map(result.map(r => [r.id, { isCompleted: r.isCompleted }]));
  } catch {
    // Borraste la lista en Recordatorios: se vuelve a crear, vacía
    listId = (await CapacitorCalendar.createRemindersList({ title: LISTA, color: COLOR })).id;
    mapa = {};
  }

  const casaDe = new Map(casas.map(c => [c.id, c]));
  const mias = tareas.filter(t => t.activa && esMia(t, casaDe.get(t.casaId), uid));
  const plan = planRecordatorios(mias, mapa, existentes);

  for (const t of plan.hechas) {
    await marcarHecha(t, yoEn(casaDe.get(t.casaId), uid)?.id ?? t.asignadoA).catch(e => console.warn('Recordatorios', e));
  }
  for (const id of plan.borrar) await CapacitorCalendar.deleteReminder({ id }).catch(() => {});
  for (const { tarea, id } of plan.cambiar) {
    await CapacitorCalendar.modifyReminder({ id, ...datos(tarea, casaDe.get(tarea.casaId)) }).catch(() => {});
  }
  for (const t of plan.crear) {
    try {
      const { alerts, ...resto } = datos(t, casaDe.get(t.casaId));
      const { id } = await CapacitorCalendar.createReminder({ listId, ...resto, ...(alerts ? { alerts } : {}) });
      plan.mapa[t.id] = { id, fecha: claveDia(t.fecha), titulo: tituloDe(t), hora: t.hora };
    } catch (e) { console.warn('Recordatorios', e); }
  }
  await guardar({ activo: true, listId, mapa: plan.mapa });
}
